/**
 * Centralized Database Module
 *
 * Single entry point for all server-side database operations.
 * Provides Supabase client initialization and health checks.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { serverConfig } from '@/server/server';

/**
 * Database connection status
 */
export enum DbStatus {
  INITIALIZED = 'initialized',
  UNCONFIGURED = 'unconfigured',
  ERROR = 'error',
}

/**
 * Database health status
 */
export interface DbHealth {
  status: 'healthy' | 'unhealthy' | 'unknown';
  message?: string;
  latency?: number;
}

/**
 * Database instance holder
 */
class DatabaseManager {
  private adminClient: SupabaseClient | null = null;
  private status: DbStatus = DbStatus.UNCONFIGURED;
  private initializationError: string | null = null;

  /**
   * Initialize the database connection
   * Should be called once during server startup
   */
  initialize(): void {
    // Check if Supabase is configured
    if (!serverConfig.supabase.url || !serverConfig.supabase.serviceRoleKey) {
      this.status = DbStatus.UNCONFIGURED;
      this.initializationError = 'Supabase URL or service role key not configured';
      console.warn('⚠️  Supabase not configured. Database features will be limited.');
      console.warn('   Set SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY environment variables.');
      return;
    }

    try {
      // Create admin client with service role key
      // This bypasses RLS for server-side operations
      this.adminClient = createClient(
        serverConfig.supabase.url,
        serverConfig.supabase.serviceRoleKey,
        {
          auth: {
            autoRefreshToken: false,
            persistSession: false,
            detectSessionInUrl: false,
          },
          db: {
            schema: 'public',
          },
        }
      );

      this.status = DbStatus.INITIALIZED;
      this.initializationError = null;
      console.log('✅ Database initialized successfully');
    } catch (error) {
      this.status = DbStatus.ERROR;
      this.initializationError = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Failed to initialize database:', error);
      throw error;
    }
  }

  /**
   * Get the admin Supabase client
   * Throws error if database is not initialized
   */
  getAdminClient(): SupabaseClient {
    if (!this.adminClient) {
      throw new Error(
        'Database not initialized. Call initializeDatabase() first.'
      );
    }
    return this.adminClient;
  }

  /**
   * Get database connection status
   */
  getStatus(): DbStatus {
    return this.status;
  }

  /**
   * Get initialization error if any
   */
  getError(): string | null {
    return this.initializationError;
  }

  /**
   * Check if database is initialized and ready
   */
  isReady(): boolean {
    return this.status === DbStatus.INITIALIZED && this.adminClient !== null;
  }

  /**
   * Perform a health check on the database
   */
  async checkHealth(): Promise<DbHealth> {
    if (!this.adminClient) {
      return {
        status: 'unhealthy',
        message: 'Database client not initialized',
      };
    }

    try {
      const startTime = Date.now();

      // Simple query to test connection
      const { error } = await this.adminClient
        .from('user_profiles')
        .select('id')
        .limit(1);

      const latency = Date.now() - startTime;

      if (error) {
        return {
          status: 'unhealthy',
          message: error.message,
          latency,
        };
      }

      return {
        status: 'healthy',
        message: 'Database connection successful',
        latency,
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create a client with user authentication context
   * For operations that need to run with specific user permissions
   *
   * @param userId - The user ID to authenticate as
   * @param userMetadata - Optional user metadata to include
   */
  createUserClient(userId: string, userMetadata?: Record<string, any>): SupabaseClient {
    if (!serverConfig.supabase.url || !serverConfig.supabase.jwtSecret) {
      throw new Error('Supabase JWT secret not configured');
    }

    // Create a JWT token for the user
    const jwt = require('jsonwebtoken');
    const token = jwt.sign(
      {
        aud: 'authenticated',
        exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour
        sub: userId,
        role: 'authenticated',
        user_metadata: userMetadata || {},
      },
      serverConfig.supabase.jwtSecret,
      {
        algorithm: 'HS256',
        issuer: 'supabase',
      }
    );

    // Create client with the user's token - use service role key as anon key for server
    const client = createClient(
      serverConfig.supabase.url,
      serverConfig.supabase.serviceRoleKey,
      {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
        },
        global: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      }
    );

    return client;
  }

  /**
   * Close database connection
   * For graceful shutdown
   */
  async close(): Promise<void> {
    this.adminClient = null;
    this.status = DbStatus.UNCONFIGURED;
    console.log('🔌 Database connection closed');
  }
}

// Export singleton instance
const databaseManager = new DatabaseManager();

/**
 * Initialize database connection
 * Call this once during server startup
 */
export function initializeDatabase(): void {
  databaseManager.initialize();
}

/**
 * Get the admin Supabase client
 * Use this for server-side operations that bypass RLS
 */
export function getDbClient(): SupabaseClient {
  return databaseManager.getAdminClient();
}

/**
 * Get database connection status
 */
export function getDbStatus(): DbStatus {
  return databaseManager.getStatus();
}

/**
 * Check if database is ready
 */
export function isDbReady(): boolean {
  return databaseManager.isReady();
}

/**
 * Perform a health check
 */
export async function checkDatabaseHealth(): Promise<DbHealth> {
  return databaseManager.checkHealth();
}

/**
 * Create a user-scoped database client
 */
export function createUserClient(userId: string, userMetadata?: Record<string, any>): SupabaseClient {
  return databaseManager.createUserClient(userId, userMetadata);
}

/**
 * Close database connection
 * Use for graceful shutdown
 */
export async function closeDatabaseConnection(): Promise<void> {
  await databaseManager.close();
}

/**
 * Get initialization error if any
 */
export function getDbError(): string | null {
  return databaseManager.getError();
}

// Export the manager for advanced use cases
export { DatabaseManager };
export default databaseManager;
